import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FlagGroup, FlagStatus, Inspection, UnderwritingStatus } from '../../../../shared/interfaces';
import { FlagService } from '../../../../flag/flag.service';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../../../dialog/dialog.service';
import { FlagManagerService } from '../../flag-manager.service';

@Component({
  selector: 'app-flag-manager-full-view',
  templateUrl: './flag-manager-full-view.component.html',
  styleUrls: ['./flag-manager-full-view.component.scss'],
})
export class FlagManagerFullViewComponent {
  constructor(
    private flagService: FlagService,
    private toastr: ToastrService,
    private flagManagerService: FlagManagerService,
  ) {}

  @Input() group: FlagGroup;
  @Input() currentFlags: any[] = [];
  @Input() inspection: Inspection;
  @Input() underwritingStatus: number = 0;
  @Input() selectedTab: string = 'included';
  @Input() issueNo: number = 0;
  @Input() allFlags: number = 0;
  @Input() tabsCount: { included: number; toResolve: number; resolved: number; excluded: number };
  @Output() editGroup = new EventEmitter<boolean>();
  @Output() selectedFlagsChange = new EventEmitter<number[]>();
  @Output() flagStatusChanged = new EventEmitter<{ flagId: number; status: number }>();
  @Output() selectedTabChange = new EventEmitter<string>();
  @Output() resolveBarTabChange = new EventEmitter<string>();
  @Output() flagIgnored = new EventEmitter<any>();
  @Output() reSeparateGroups = new EventEmitter<any>();

  showableFlags: any[] = [];
  isIncludedGroup: boolean = false;
  resolveBarSelectedTab: string;
  selectedFlagIds: number[] = [];
  get included() {
    if (!this.group) {
      return [];
    }
    return this.currentFlags.filter(f => !f.ignore);
  }

  get unResolved() {
    return this.currentFlags.filter(f => f.status === FlagStatus.UNRESOLVED && !f.ignore && f.groupId);
  }
  get partialResponse() {
    return this.currentFlags.filter(f => f.status === FlagStatus.PARTIAL_RESPONSE && !f.ignore && f.groupId);
  }
  get resolved() {
    return this.currentFlags.filter(f => f.status === FlagStatus.RESOLVED && !f.ignore && f.groupId);
  }
  get ignored() {
    if (!this.group) {
      return this.currentFlags;
    }
    return this.currentFlags.filter(f => f.ignore);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['group']) {
      this.setDefaultResolvedTab();
    }

    if (changes['currentFlags'] || changes['underwritingStatus']) {
      this.showFlags();
    }
  }

  onTabChanged(tab: string): void {
    this.selectedTab = tab;
    this.checkResolvedTab();
    this.flagManagerService.deselectAllFlags();
    this.selectedFlagsChange.emit(this.selectedFlagIds);
    this.selectedTabChange.emit(this.selectedTab);
    this.resolveBarTabChange.emit(this.resolveBarSelectedTab);
    this.showFlags();
  }

  checkResolvedTab() {
    if (this.selectedTab === 'toResolve') {
      if (!this.resolveBarSelectedTab) {
        this.setDefaultResolvedTab();
      }
    }
  }

  setDefaultResolvedTab() {
    if (this.unResolved.length > 0) {
      this.resolveBarSelectedTab = 'unresolved';
    } else {
      this.resolveBarSelectedTab = 'partial';
    }
    this.resolveBarTabChange.emit(this.resolveBarSelectedTab);
  }

  private showFlags(): void {
    switch (this.selectedTab) {
      case 'included':
        this.showableFlags = this.included;
        break;
      case 'excluded':
        this.showableFlags = this.ignored;
        break;
      case 'toResolve':
        if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) {
          if (this.resolveBarSelectedTab === 'partial') {
            this.showableFlags = [...this.partialResponse];
          } else {
            this.showableFlags = [...this.unResolved];
          }
        } else this.showableFlags = [];
        break;
      case 'resolved':
        if (this.underwritingStatus >= UnderwritingStatus.RESOLUTION_IN_PROGRESS) this.showableFlags = this.resolved;
        else this.showableFlags = [];
        break;
      default:
        console.warn(`Unknown tab selected: ${this.selectedTab}`);
        this.showableFlags = [];
    }
  }

  onSelectedFlagsChange(selectedFlagIds: number[]) {
    this.selectedFlagIds = selectedFlagIds;
    this.selectedFlagsChange.emit(selectedFlagIds);
  }

  shouldShowResolveTopbar(): boolean {
    return this.selectedTab === 'toResolve';
  }

  getTotalResolvableFlags(): number {
    return this.unResolved.length + this.partialResponse.length + this.resolved.length;
  }

  getResolveSelectedTab(): string {
    if (this.selectedTab === 'toResolve') {
      return this.resolveBarSelectedTab || 'unresolved';
    }
    return 'unresolved';
  }

  onResolveTabChange(tabKey: string): void {
    this.resolveBarSelectedTab = tabKey;
    if (tabKey === 'unresolved') {
      this.showableFlags = this.unResolved;
    } else if (tabKey === 'partial') {
      this.showableFlags = this.partialResponse;
    }
    this.flagManagerService.deselectAllFlags();
    this.resolveBarTabChange.emit(tabKey);
    this.showFlags();
  }

  onFlagStatusChange(event: { flagId: number; status: number }) {
    const flag = this.currentFlags.find(f => f.id === event.flagId);
    if (flag) {
      this.changeFlagStatus(flag, event.status);
    }
  }

  async changeFlagStatus(flag: any, status: FlagStatus) {
    try {
      flag.status = status;
      if (flag.flag) {
        flag.flag.status = status;
      }

      const cleanFlag = { ...flag };
      delete cleanFlag.checkbox;

      await this.flagService.edit(flag.id, cleanFlag);

      // Fetch updated flag with latest changes
      const updatedFlag = await this.flagService.get(flag.id);
      flag.changes = updatedFlag.changes;
      if (flag.flag) {
        flag.flag.changes = updatedFlag.changes;
      }

      this.flagStatusChanged.emit({ flagId: flag.id, status: status });
      this.showFlags();
    } catch (error) {
      console.error('Error updating flag status:', error);
      this.toastr.error('Failed to update flag status');
      flag.status = flag.flag ? flag.flag.status : flag.status;
    }
  }

  async onFlagIgnored(flag: any) {
    try {
      const cleanFlag = { ...flag };
      delete cleanFlag.checkbox;
      await this.flagService.edit(flag.id, cleanFlag);

      // Fetch updated flag with latest changes
      const updatedFlag = await this.flagService.get(flag.id);
      flag.changes = updatedFlag.changes;
      if (flag.flag) {
        flag.flag.changes = updatedFlag.changes;
      }

      this.flagIgnored.emit(flag);

      // Show appropriate message based on ignore status
    } catch (error) {
      console.error('Error updating flag ignore status:', error);
      this.toastr.error('Failed to update flag status');
      // Revert the ignore state
      flag.ignore = !flag.ignore;
      if (flag.flag) {
        flag.flag.ignore = !flag.flag.ignore;
      }
      return; // Exit early on error
    }

    // Update group flags for real-time updates
    this.updateGroupAndHandleTabSwitching();
  }

  private updateGroupAndHandleTabSwitching(): void {
    // Update group flags
    this.group.flags = this.currentFlags;

    // Handle automatic tab switching when current tab becomes empty
    const shouldSwitchFromIncluded = this.selectedTab === 'included' && this.included.length === 0;
    const shouldSwitchFromExcluded = this.selectedTab === 'excluded' && this.ignored.length === 0;

    if (shouldSwitchFromIncluded) {
      this.switchToTab('excluded');
    } else if (shouldSwitchFromExcluded) {
      this.switchToTab('included');
    }

    // emit group update for re-separation to update in the sidebar
    this.reSeparateGroups.emit(this.group);
  }

  private switchToTab(newTab: string): void {
    this.selectedTab = newTab;
    this.onTabChanged(this.selectedTab);
  }
}
